/**
 * Pre-Campaign Country Permission & Number Validation Helper
 * Validates recipient phone numbers against user account country permissions
 */

export const PREFIX_LIST = [
  { code: "852", name: "Hong Kong" },
  { code: "971", name: "United Arab Emirates" },
  { code: "972", name: "Israel" },
  { code: "974", name: "Qatar" },
  { code: "234", name: "Nigeria" },
  { code: "966", name: "Saudi Arabia" },
  { code: "91", name: "India" },
  { code: "44", name: "United Kingdom" },
  { code: "54", name: "Argentina" },
  { code: "55", name: "Brazil" },
  { code: "56", name: "Chile" },
  { code: "57", name: "Colombia" },
  { code: "20", name: "Egypt" },
  { code: "33", name: "France" },
  { code: "49", name: "Germany" },
  { code: "36", name: "Hungary" },
  { code: "62", name: "Indonesia" },
  { code: "39", name: "Italy" },
  { code: "60", name: "Malaysia" },
  { code: "52", name: "Mexico" },
  { code: "31", name: "Netherlands" },
  { code: "92", name: "Pakistan" },
  { code: "51", name: "Peru" },
  { code: "48", name: "Poland" },
  { code: "40", name: "Romania" },
  { code: "65", name: "Singapore" },
  { code: "27", name: "South Africa" },
  { code: "34", name: "Spain" },
  { code: "90", name: "Turkey" },
  { code: "1", name: "USA / Canada" },
  { code: "7", name: "Russia" },
]

/**
 * Detect Country Code & Name from any input mobile number
 * Handles Edge Cases:
 * 1. Indian numbers without +91 (10 digits starting with 6-9)
 * 2. Indian numbers starting with 91 (12 digits starting 91)
 * 3. Numbers with + prefix (+1, +44, +971, +91)
 * 4. Spaces, brackets, hyphens cleaning
 * 5. International numbers with +CountryCode
 */
export function detectNumberCountry(rawNumber) {
  if (!rawNumber) return { code: "91", name: "India", clean: "" }

  let cleaned = String(rawNumber).trim().replace(/[\s\-\(\)\.]/g, "")
  if (cleaned.startsWith("+")) {
    cleaned = cleaned.substring(1)
  }

  // Handle 10-digit Indian number without 91 prefix
  if (cleaned.length === 10 && /^[6-9]\d{9}$/.test(cleaned)) {
    return { code: "91", name: "India", clean: `91${cleaned}` }
  }

  // Handle 12-digit Indian number starting with 91
  if (cleaned.length === 12 && cleaned.startsWith("91") && /^91[6-9]\d{9}$/.test(cleaned)) {
    return { code: "91", name: "India", clean: cleaned }
  }

  // Match against sorted prefix list (longest prefix first)
  for (const item of PREFIX_LIST) {
    if (cleaned.startsWith(item.code)) {
      return { code: item.code, name: item.name, clean: cleaned }
    }
  }

  // Default fallback if unknown
  return { code: "other", name: "International / Other", clean: cleaned }
}

/**
 * Validate country permissions for a batch of mobile numbers against user settings
 * @param {Array<string|object>} numbers - List of raw numbers or recipient objects
 * @param {object} user - Logged in user object
 * @returns {object} Summary of authorized vs unauthorized countries
 */
export function validateCountryPermissions(numbers, user) {
  const settings = user?.settings || {}
  const billing = settings.billing || {}
  const allowInternational = settings.allowInternational ?? billing.allowInternational ?? user?.allowInternational ?? false
  const allowedCountries = settings.allowedCountries || billing.allowedCountries || user?.allowedCountries || ["91"]

  const countryCounts = {}

  for (const item of numbers) {
    const rawNum = typeof item === "string" ? item : item?.number || item?.phone || item?.mobile || item?.to || ""
    if (!rawNum) continue

    const country = detectNumberCountry(rawNum)
    const key = country.code

    if (!countryCounts[key]) {
      countryCounts[key] = {
        code: country.code,
        name: country.name,
        count: 0,
        isAllowed: country.code === "91" || allowInternational || allowedCountries.includes(country.code),
      }
    }
    countryCounts[key].count++
  }

  const authorizedCountries = []
  const unauthorizedCountries = []

  for (const item of Object.values(countryCounts)) {
    if (item.isAllowed) {
      authorizedCountries.push(item)
    } else {
      unauthorizedCountries.push(item)
    }
  }

  return {
    hasUnauthorized: unauthorizedCountries.length > 0,
    hasInternational: Object.values(countryCounts).some((c) => c.code !== "91"),
    authorizedCountries,
    unauthorizedCountries,
    totalNumbers: numbers.length,
  }
}

/**
 * Pre-validation SweetAlert dialog logic according to Spec Sections 1-10
 * @param {Array<string|object>} numbersList
 * @param {object} user
 * @param {object} SwalInstance
 * @returns {Promise<boolean>} True if campaign launch should proceed, false if aborted
 */
export async function checkCampaignCountryPermissions(numbersList, user, SwalInstance) {
  if (!numbersList || numbersList.length === 0) return true
  const Swal = SwalInstance || (typeof window !== "undefined" ? window.Swal : null)
  if (!Swal) return true

  const analysis = validateCountryPermissions(numbersList, user)

  // If no international numbers at all (all India), proceed directly!
  if (!analysis.hasInternational && !analysis.hasUnauthorized) {
    return true
  }

  const formatList = (countries) =>
    countries
      .map(
        (c) =>
          `<li style="text-align: left; margin-bottom: 3px;"><b>${c.name}</b> (+${c.code}) – <b>${c.count.toLocaleString()}</b> numbers</li>`
      )
      .join("")

  // Scenario A: Unauthorized countries present (Section 3, 5, 9)
  if (analysis.hasUnauthorized) {
    let htmlMsg = `<div style="text-align: left; font-size: 13px; line-height: 1.6;">`

    if (analysis.authorizedCountries.length > 0) {
      htmlMsg += `<div style="margin-bottom: 12px;"><b style="color: #10b981; font-size: 14px;">Permission Enabled:</b><ul style="margin-top: 4px; padding-left: 20px;">`
      htmlMsg += formatList(analysis.authorizedCountries)
      htmlMsg += `</ul></div>`
    }

    htmlMsg += `<div style="margin-bottom: 12px;"><b style="color: #ef4444; font-size: 14px;">Permission Not Enabled:</b><ul style="margin-top: 4px; padding-left: 20px;">`
    htmlMsg += formatList(analysis.unauthorizedCountries)
    htmlMsg += `</ul></div>`

    const unauthNames = analysis.unauthorizedCountries.map((c) => c.name).join(", ")
    htmlMsg += `<p style="margin-top: 12px; color: #374151; font-weight: 500;">`
    htmlMsg += `You do not have permission to send messages to <b>${unauthNames}</b>.<br/>Please contact Support to enable messaging permission for these countries.`
    htmlMsg += `</p></div>`

    // If NO authorized numbers exist at all (e.g. only UK numbers and account has no UK permission)
    if (analysis.authorizedCountries.length === 0) {
      await Swal.fire({
        title: "International Sending Permission Required",
        html: htmlMsg,
        icon: "warning",
        confirmButtonColor: "#ef4444",
        confirmButtonText: "OK",
      })
      return false // Abort campaign sending
    }

    // Mixed permission scenario: User can choose whether to proceed with authorized numbers only
    const result = await Swal.fire({
      title: "International Sending Permission",
      html: htmlMsg,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Continue (Authorized Only)",
      cancelButtonText: "Cancel",
    })

    return result.isConfirmed
  }

  // Scenario B: All International Countries Authorized (Section 4, 9)
  let authHtml = `<div style="text-align: left; font-size: 13px; line-height: 1.6;">`
  authHtml += `<p style="margin-bottom: 8px; font-weight: 500;">This campaign contains international mobile numbers from:</p>`
  authHtml += `<ul style="margin-top: 4px; padding-left: 20px;">`
  authHtml += formatList(analysis.authorizedCountries.filter((c) => c.code !== "91"))
  authHtml += `</ul>`
  authHtml += `<p style="margin-top: 12px; color: #374151;">Your account has permission to send messages to these countries.<br/>Do you want to continue sending the campaign?</p>`
  authHtml += `</div>`

  const authResult = await Swal.fire({
    title: "International Numbers Detected",
    html: authHtml,
    icon: "info",
    showCancelButton: true,
    confirmButtonColor: "#10b981",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Continue",
    cancelButtonText: "Cancel",
  })

  return authResult.isConfirmed
}
